// src/components/health/dashboard/HealthInsights.tsx
// AI-Generated Health Insights Panel

import React from 'react';
import { motion } from 'framer-motion';
import { 
  Lightbulb, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Info,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import type { ReportInsight } from '@/features/health/types/healthTypes/reportTypes';

// ============================================================================
// Types
// ============================================================================

interface HealthInsightsProps {
  insights: ReportInsight[];
  onViewReports: () => void;
}

// ============================================================================
// Component
// ============================================================================

export const HealthInsights: React.FC<HealthInsightsProps> = ({
  insights,
  onViewReports,
}) => {
  const sortedInsights = [...insights].sort((a, b) => {
    const priority: Record<ReportInsight['type'], number> = {
      alert: 1,
      negative: 2,
      positive: 3,
      neutral: 4,
    };
    return priority[a.type] - priority[b.type];
  });

  return (
    <div className="bg-white rounded-xl border border-cream-dark h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-cream-dark">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sage to-sage-dark flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-navy">Insights</h3>
            <p className="text-sm text-warm-gray">AI-powered analysis</p>
          </div>
        </div>
      </div>

      {/* Insights List */}
      <div className="flex-1 p-4 overflow-y-auto">
        {insights.length === 0 ? (
          <EmptyInsightsState />
        ) : (
          <div className="space-y-3">
            {sortedInsights.slice(0, 5).map((insight, index) => (
              <InsightCard key={insight.id} insight={insight} index={index} />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-cream-dark">
        <button
          onClick={onViewReports}
          className="
            w-full flex items-center justify-center gap-2
            py-2.5 px-4 rounded-lg
            bg-cream hover:bg-cream-dark
            text-navy font-medium text-sm
            transition-colors duration-200
          "
        >
          <Lightbulb className="w-4 h-4" />
          View Full Report
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Insight Card Component
// ============================================================================

interface InsightCardProps {
  insight: ReportInsight;
  index: number;
}

const InsightCard: React.FC<InsightCardProps> = ({ insight, index }) => {
  const config = getInsightConfig(insight.type);
  const IconComponent = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.3 }}
      className={`
        p-4 rounded-lg border
        ${config.bgClass} ${config.borderClass}
      `}
    >
      <div className="flex items-start gap-3">
        <div className={`
          flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center
          ${config.iconBgClass}
        `}>
          <IconComponent className={`w-4 h-4 ${config.iconClass}`} />
        </div>

        <div className="flex-1 min-w-0">
          <h4 className={`font-medium text-sm ${config.titleClass}`}>
            {insight.title}
          </h4>
          <p className="text-sm text-warm-gray mt-1 line-clamp-2">
            {insight.description}
          </p>

          {/* Metric Display */}
          {insight.metric && insight.value !== undefined && (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-warm-gray">{insight.metric}:</span>
              <span className={`text-sm font-semibold ${config.titleClass}`}>
                {insight.value}
              </span>
            </div>
          )}

          {/* Recommendation */}
          {insight.recommendation && (
            <div className="mt-3 pt-3 border-t border-dashed border-warm-gray/20">
              <p className="text-xs text-warm-gray">
                <span className="font-medium">Tip:</span> {insight.recommendation}
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ============================================================================
// Empty State
// ============================================================================

const EmptyInsightsState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-8 text-center">
    <div className="w-14 h-14 rounded-full bg-cream flex items-center justify-center mb-4">
      <Lightbulb className="w-7 h-7 text-warm-gray" />
    </div>
    <p className="font-medium text-navy">No insights yet</p>
    <p className="text-sm text-warm-gray mt-1 max-w-[200px]">
      Keep logging health data to receive personalized insights.
    </p>
  </div>
);

// ============================================================================
// Configuration
// ============================================================================

interface InsightConfig {
  icon: React.FC<{ className?: string }>;
  bgClass: string;
  borderClass: string;
  iconBgClass: string;
  iconClass: string;
  titleClass: string;
}

function getInsightConfig(type: ReportInsight['type']): InsightConfig {
  const configs: Record<ReportInsight['type'], InsightConfig> = {
    positive: {
      icon: CheckCircle2,
      bgClass: 'bg-success/5',
      borderClass: 'border-success/20',
      iconBgClass: 'bg-success/10',
      iconClass: 'text-success',
      titleClass: 'text-navy',
    },
    negative: {
      icon: TrendingDown,
      bgClass: 'bg-terracotta/5',
      borderClass: 'border-terracotta/20',
      iconBgClass: 'bg-terracotta/10',
      iconClass: 'text-terracotta',
      titleClass: 'text-navy',
    },
    neutral: {
      icon: Info,
      bgClass: 'bg-cream',
      borderClass: 'border-cream-dark',
      iconBgClass: 'bg-navy/10',
      iconClass: 'text-navy',
      titleClass: 'text-navy',
    },
    alert: {
      icon: AlertTriangle,
      bgClass: 'bg-red-50',
      borderClass: 'border-red-200',
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      titleClass: 'text-red-900',
    },
  };

  return configs[type];
}

export default HealthInsights;

